// AppointmentBooking.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { db, auth } from "./firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  runTransaction,
  onSnapshot,   // 👈 ADD THIS
} from "firebase/firestore";

import { useAuthState } from "react-firebase-hooks/auth";
import { Video, Phone, Building2 } from "lucide-react";
import {
  Calendar,
  Clock,
  User,
  X,
  AlertCircle,
  Stethoscope,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";

/**
 * Data model:
 * - Doctor doc: "doctors/{id}" has workingHours.{monday..sunday} {enabled,start,end}, services[], name, specialty, location.address
 *
 * - Public availability:
 *    - "locks/{doctorId}_{YYYY-MM-DD}_{HH:mm}" (no patient data)
 *      fields: { doctorId, scheduledDate, scheduledTime, appointmentType, status, createdAt }
 *        - status: "pending" | "confirmed" | "cancelled"
 *    - "doctorBusy/{doctorId}/blocks/{blockId}"
 *      fields: { date, startTime, endTime, type, notes?, createdAt, updatedAt }
 *
 * - Private appointments:
 *    - "appointments/{appointmentId}"
 *      full patient data, reason, notes, etc. (protected by security rules)
 */

const WEEK_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const DEFAULT_DURATION_MIN = 30;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
}

function getWeekKey(date) {
  return WEEK_KEYS[date.getDay()];
}

/** Albanian + EN duration parser */
function parseDurationToMinutes(str) {
  if (!str || typeof str !== "string") return DEFAULT_DURATION_MIN;
  const s = str
    .toLowerCase()
    .trim()
    .replaceAll("orë", "ore")
    .replaceAll("minuta", "min")
    .replaceAll("minute", "min")
    .replaceAll("minutes", "min")
    .replaceAll("mins", "min")
    .replaceAll("hour", "h")
    .replaceAll("hours", "h");

  const range = s.match(/(\d+)\s*-\s*(\d+)\s*(h|ore|min|m)?/);
  if (range) {
    const n = parseInt(range[1], 10);
    const unit =
      range[3] || (s.includes("ore") ? "ore" : s.includes("h") ? "h" : "min");
    if (unit === "h" || unit === "ore") return n * 60;
    return n;
  }

  const m1 = s.match(/(\d+)\s*(min|m)?$/);
  if (m1) return parseInt(m1[1], 10);

  const m2 = s.match(/(\d+)\s*m\b/);
  if (m2) return parseInt(m2[1], 10);

  const h1 = s.match(/(\d+)\s*(h|ore)\b/);
  if (h1) return parseInt(h1[1], 10) * 60;

  const h = s.match(/(\d+)\s*h/);
  const mm = s.match(/(\d+)\s*m/);
  if (h || mm) {
    const hours = h ? parseInt(h[1], 10) : 0;
    const mins = mm ? parseInt(mm[1], 10) : 0;
    const total = hours * 60 + mins;
    return total > 0 ? total : DEFAULT_DURATION_MIN;
  }

  return DEFAULT_DURATION_MIN;
}

function makeDateList(days, hours) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < days; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const key = getWeekKey(d);
    const cfg = hours?.[key];
    if (!cfg || !cfg.enabled) continue;
    out.push({
      date: toYmd(d),
      displayDate: d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      fullDate: d,
    });
  }
  return out;
}

/** Iterate slots where (slotStart + duration) <= end */
function* boundedSlots(startHHmm, endHHmm, stepMin, durationMin) {
  const [sh, sm] = startHHmm.split(":").map((n) => parseInt(n, 10));
  const [eh, em] = endHHmm.split(":").map((n) => parseInt(n, 10));
  let cur = new Date(2000, 0, 1, sh, sm, 0, 0);
  const end = new Date(2000, 0, 1, eh, em, 0, 0);
  while (true) {
    const slotEnd = new Date(cur.getTime() + durationMin * 60 * 1000);
    if (slotEnd > end) break;
    const hh = pad2(cur.getHours());
    const mm = pad2(cur.getMinutes());
    yield `${hh}:${mm}`;
    cur = new Date(cur.getTime() + stepMin * 60 * 1000);
  }
}

/** Build a Date from local YYYY-MM-DD + HH:mm */
function buildLocalDate(ymd, hhmm) {
  const [Y, M, D] = ymd.split("-").map((n) => parseInt(n, 10));
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  return new Date(Y, M - 1, D, h, m, 0, 0);
}

const AppointmentBooking = ({
  doctor,
  selectedService = null,
  onClose,
  onSuccess,
}) => {
  const [user] = useAuthState(auth);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [doctorDoc, setDoctorDoc] = useState(null);
  const [slots, setSlots] = useState([]);

  const [bookingData, setBookingData] = useState({
    serviceId: selectedService?.id || "",
    serviceName: selectedService?.title || "",
    servicePrice: selectedService?.price || "",
    serviceDuration: selectedService?.duration || "30 minutes",
    selectedDate: "",
    selectedTime: "",
    reason: "",
    notes: "",
    contactPhone: "",
    appointmentType: "in-person", // 'in-person' | 'video-call' | 'phone-call'
  });

  const updateBookingData = (k, v) =>
    setBookingData((p) => ({
      ...p,
      [k]: v,
    }));

  const doctorId = useMemo(
    () => doctor?.uid || doctor?.id || null,
    [doctor]
  );

  /** Fetch fresh doctor doc (services + workingHours) */
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!doctorId) return;
      try {
        const snap = await getDoc(doc(db, "doctors", doctorId));
        if (!ignore && snap.exists()) {
          const d = snap.data();
          setDoctorDoc(d);

          // Autofill service if not provided
          if (
            !bookingData.serviceName &&
            Array.isArray(d?.services) &&
            d.services.length
          ) {
            const s = d.services[0] || {};
            setBookingData((p) => ({
              ...p,
              serviceId: s.id || "0",
              serviceName: s.title || "",
              servicePrice: s.price || "",
              serviceDuration: s.duration || "30 minutes",
            }));
          }
        }
      } catch (err) {
        console.warn("Doctor load error:", err);
      }
    })();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  /** Available dates (next 30 days, only open days) */
  const availableDates = useMemo(
    () => makeDateList(30, doctorDoc?.workingHours || {}),
    [doctorDoc?.workingHours]
  );

  /**
   * Build slots for selected day:
   * - based on doctor's workingHours
   * - mark as unavailable if:
   *    - overlaps any doctorBusy block (manual busy)
   *    - or present in locks with status != "cancelled"
   * - DO NOT read from appointments (they’re private)
   */
  const buildSlots = useCallback(
    async (ymd, durationMin) => {
      const d = ymd ? new Date(ymd + "T00:00:00") : null;
      if (!d || !doctorDoc?.workingHours || !doctorId) {
        setSlots([]);
        return;
      }

      const cfg = doctorDoc.workingHours[getWeekKey(d)];
      if (!cfg || !cfg.enabled) {
        setSlots([]);
        return;
      }

      const startHHmm = cfg.start || "09:00";
      const endHHmm = cfg.end || "17:00";

      /* ---------- 1) LOAD BUSY BLOCKS (doctorBusy) ---------- */
      const busyBlocks = [];
      try {
        const qBusy = query(
          collection(db, "doctorBusy", doctorId, "blocks"),
          where("date", "==", ymd)
        );
        const busySnap = await getDocs(qBusy);
        busySnap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (data.startTime && data.endTime) {
            busyBlocks.push({
              startTime: data.startTime,
              endTime: data.endTime,
            });
          }
        });
      } catch (err) {
        console.warn("Busy blocks load error:", err);
      }

      const isSlotBusy = (hhmm) => {
        const [sh, sm] = hhmm.split(":").map((n) => parseInt(n, 10));
        const slotStartMinutes = sh * 60 + sm;
        const slotEndMinutes = slotStartMinutes + durationMin;

        return busyBlocks.some((block) => {
          const [bh, bm] = (block.startTime || "00:00")
            .split(":")
            .map((n) => parseInt(n, 10));
          const [eh, em] = (block.endTime || "23:59")
            .split(":")
            .map((n) => parseInt(n, 10));
          const blockStart = bh * 60 + bm;
          const blockEnd = eh * 60 + em;
          return (
            slotStartMinutes < blockEnd && slotEndMinutes > blockStart
          );
        });
      };

      /* ---------- 2) LOAD PUBLIC TAKEN SLOTS (locks) ---------- */
      const lockedTimes = new Set();
      try {
        const qLocks = query(
          collection(db, "locks"),
          where("doctorId", "==", doctorId),
          where("scheduledDate", "==", ymd)
        );
        const lockSnap = await getDocs(qLocks);
        lockSnap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const status = data.status || "pending";
          // pending + confirmed = blocked; cancelled = free
          if (data.scheduledTime && status !== "cancelled") {
            lockedTimes.add(data.scheduledTime);
          }
        });
      } catch (err) {
        console.warn("Locks load error:", err);
      }

      /* ---------- 3) BUILD SLOTS ---------- */
      const now = new Date();
      const isToday = toYmd(now) === ymd;

      const fresh = [];
      for (const hhmm of boundedSlots(
        startHHmm,
        endHHmm,
        durationMin,
        durationMin
      )) {
        if (isToday) {
          const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
          const slotDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            h,
            m,
            0,
            0
          );
          if (slotDate <= now) continue;
        }

        const busy = isSlotBusy(hhmm);
        const locked = lockedTimes.has(hhmm);

        fresh.push({
          time: hhmm,
          displayTime: new Date(`2000-01-01T${hhmm}:00`).toLocaleTimeString(
            undefined,
            { hour: "numeric", minute: "2-digit" }
          ),
          available: !busy && !locked,
        });
      }

      setSlots(fresh);
    },
    [doctorDoc?.workingHours, doctorId]
  );

  /** Rebuild slots whenever date or duration changes */
  useEffect(() => {
    if (!bookingData.selectedDate) {
      setSlots([]);
      return;
    }
    const dur = parseDurationToMinutes(bookingData.serviceDuration);
    buildSlots(bookingData.selectedDate, dur);
  }, [bookingData.selectedDate, bookingData.serviceDuration, buildSlots]);
  /** Live reaction to lock status changes (pending / confirmed / cancelled) */
  useEffect(() => {
    if (!doctorId || !bookingData.selectedDate) return;

    const qLocksLive = query(
      collection(db, "locks"),
      where("doctorId", "==", doctorId),
      where("scheduledDate", "==", bookingData.selectedDate)
    );

    const unsubscribe = onSnapshot(qLocksLive, () => {
      // whenever a lock is added/updated/deleted for that day,
      // rebuild the slots list so UI reflects the change
      const dur = parseDurationToMinutes(bookingData.serviceDuration);
      buildSlots(bookingData.selectedDate, dur);
    });

    return () => unsubscribe();
  }, [doctorId, bookingData.selectedDate, bookingData.serviceDuration, buildSlots]);

  /** Autofill phone once */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      let phone = user.phoneNumber || "";
      try {
        const uSnap = await getDoc(doc(db, "users", user.uid));
        if (uSnap.exists()) {
          const u = uSnap.data() || {};
          phone =
            u.phone || u.contactPhone || u.mobile || u.phoneNumber || phone;
        }
      } catch {}
      if (!phone && user.email) {
        const handle = user.email.split("@")[0];
        if (/^\+?\d/.test(handle)) phone = handle;
      }
      if (!cancelled && phone) {
        setBookingData((prev) =>
          prev.contactPhone ? prev : { ...prev, contactPhone: phone }
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  /** Validate steps */
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return !!bookingData.serviceName;
      case 2:
        return !!bookingData.selectedDate;
      case 3:
        return !!bookingData.selectedTime;
      case 4:
        return (
          bookingData.reason.trim().length > 0 &&
          /^\+?\d[\d\s-]{6,}$/.test(bookingData.contactPhone)
        );
      default:
        return true;
    }
  };

  /** Submit with transactional lock to avoid race double-bookings */
  const submitBooking = async () => {
    if (!user) {
      setError("Please log in to book an appointment");
      return;
    }
    if (!doctorId) {
      setError("Doctor information is incomplete. Please try again.");
      return;
    }
    if (!validateStep(3)) {
      setError("Please complete all required fields.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // resolve patient name
      let patientName = user.displayName?.trim();
      if (!patientName) {
        try {
          const u = await getDoc(doc(db, "users", user.uid));
          if (u.exists()) {
            const d = u.data() || {};
            patientName =
              d.fullName ||
              d.name ||
              d.displayName ||
              (d.firstName && d.lastName
                ? `${d.firstName} ${d.lastName}`
                : null);
          }
        } catch {}
        if (!patientName) {
          patientName = user.email?.split("@")[0] || "Patient";
        }
      }

      const ymd = bookingData.selectedDate;
      const hhmm = bookingData.selectedTime;
      const scheduledAt = buildLocalDate(ymd, hhmm);

      // PRIVATE appointment data
      const appointmentData = {
        patientId: user.uid,
        patientInfo: {
          name: patientName,
          email: user.email || "",
          phone: bookingData.contactPhone,
        },
        doctorId,
        doctorInfo: {
          name: doctor?.name || doctorDoc?.name || "Doctor",
          specialty: doctor?.specialty || doctorDoc?.specialty || "General",
          location:
            doctor?.location?.address || doctorDoc?.location?.address || "",
        },
        serviceId: bookingData.serviceId,
        serviceName: bookingData.serviceName,
        servicePrice: bookingData.servicePrice,
        serviceDuration: bookingData.serviceDuration,
        appointmentType: bookingData.appointmentType,
        scheduledDate: ymd,
        scheduledTime: hhmm,
        scheduledAt,
        reason: bookingData.reason,
        notes: bookingData.notes,
        status: "pending", // appointment side
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // PUBLIC lock (no patient data)
      const lockId = `${doctorId}_${ymd}_${hhmm}`;
      const lockRef = doc(db, "locks", lockId);

           await runTransaction(db, async (tx) => {
        const lockSnap = await tx.get(lockRef);

        if (lockSnap.exists()) {
          const existing = lockSnap.data() || {};
          const status = existing.status || "pending";

          // If lock is NOT cancelled → treat as taken
          if (status !== "cancelled") {
            throw new Error(
              "This time was just taken. Please pick another slot."
            );
          }

          // If lock is cancelled → reuse same doc for new booking
          tx.update(lockRef, {
            doctorId,
            scheduledDate: ymd,
            scheduledTime: hhmm,
            appointmentType: bookingData.appointmentType,
            status: "pending",
            updatedAt: serverTimestamp(),
            // keep original createdAt if it exists, otherwise set it
            ...(existing.createdAt
              ? { createdAt: existing.createdAt }
              : { createdAt: serverTimestamp() }),
          });
        } else {
          // First time booking this slot → normal create
          tx.set(lockRef, {
            doctorId,
            scheduledDate: ymd,
            scheduledTime: hhmm,
            appointmentType: bookingData.appointmentType,
            status: "pending",
            createdAt: serverTimestamp(),
          });
        }

        // 3) create appointment (private)
        const newRef = doc(collection(db, "appointments"));
        tx.set(newRef, appointmentData);
      });


      setSuccess(true);
      setLoading(false);
      setTimeout(() => {
        onSuccess && onSuccess(lockId);
      }, 1200);
    } catch (e) {
      console.error(e);
      setError(
        e?.message || "Failed to book appointment. Please try again."
      );
      setLoading(false);
      if (bookingData.selectedDate) {
        const dur = parseDurationToMinutes(bookingData.serviceDuration);
        await buildSlots(bookingData.selectedDate, dur);
      }
    }
  };

  // ===== inline styles & UI =====
  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: 20,
    },
    modal: {
      backgroundColor: "white",
      borderRadius: 24,
      maxWidth: 680,
      width: "100%",
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
      position: "relative",
    },
    header: {
      padding: "28px 28px 18px",
      borderBottom: "1px solid #e5e7eb",
      position: "sticky",
      top: 0,
      backgroundColor: "white",
      zIndex: 10,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
    },
    headerTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12,
    },
    title: {
      fontSize: 22,
      fontWeight: 700,
      color: "#111827",
      margin: 0,
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: 22,
      cursor: "pointer",
      color: "#6b7280",
      padding: 6,
      borderRadius: 8,
    },
    progressBar: {
      display: "flex",
      gap: 8,
      marginBottom: 8,
    },
    progressStep: {
      flex: 1,
      height: 4,
      borderRadius: 2,
      transition: "all .3s",
    },
    stepInfo: {
      fontSize: 13,
      color: "#6b7280",
      textAlign: "center",
    },
    content: {
      padding: 28,
    },
    stepTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: "#111827",
      marginBottom: 8,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    stepDescription: {
      fontSize: 14,
      color: "#6b7280",
      marginBottom: 18,
    },
    serviceCard: {
      padding: 16,
      border: "2px solid #e5e7eb",
      borderRadius: 12,
      cursor: "pointer",
      transition: "all .2s",
      marginBottom: 10,
    },
    serviceCardSelected: {
      borderColor: "#3b82f6",
      backgroundColor: "#eff6ff",
    },
    dateGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
      gap: 10,
      marginBottom: 16,
    },
    dateCard: {
      padding: "14px 10px",
      border: "2px solid #e5e7eb",
      borderRadius: 12,
      textAlign: "center",
      cursor: "pointer",
      transition: "all .2s",
    },
    dateCardSelected: {
      borderColor: "#3b82f6",
      backgroundColor: "#eff6ff",
    },
    timeGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
      gap: 8,
    },
    timeSlot: {
      padding: "10px 8px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      textAlign: "center",
      cursor: "pointer",
      transition: "all .2s",
      fontSize: 14,
    },
    timeSlotSelected: {
      borderColor: "#3b82f6",
      backgroundColor: "#3b82f6",
      color: "white",
    },
    timeSlotUnavailable: {
      backgroundColor: "#f3f4f6",
      color: "#9ca3af",
      cursor: "not-allowed",
    },
    formGroup: {
      marginBottom: 16,
    },
    label: {
      display: "block",
      fontSize: 14,
      fontWeight: 500,
      color: "#374151",
      marginBottom: 6,
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      border: "2px solid #e5e7eb",
      borderRadius: 10,
      fontSize: 16,
      outline: "none",
      boxSizing: "border-box",
    },
    iconBadge: {
      width: 36,
      height: 36,
      borderRadius: 999,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    },
    iconInPerson: { backgroundColor: "#eef2ff", color: "#4f46e5" },
    iconVideo: { backgroundColor: "#eff6ff", color: "#2563eb" },
    iconPhone: { backgroundColor: "#ecfeff", color: "#0891b2" },
    textarea: {
      width: "100%",
      padding: "12px 14px",
      border: "2px solid #e5e7eb",
      borderRadius: 10,
      fontSize: 16,
      outline: "none",
      resize: "vertical",
      minHeight: 100,
      boxSizing: "border-box",
    },
    typeSelector: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: 10,
      marginBottom: 14,
    },
    typeOption: {
      padding: 14,
      border: "2px solid #e5e7eb",
      borderRadius: 12,
      textAlign: "center",
      cursor: "pointer",
    },
    typeOptionSelected: {
      borderColor: "#3b82f6",
      backgroundColor: "#eff6ff",
    },
    footer: {
      padding: "18px 28px",
      borderTop: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      backgroundColor: "#f9fafb",
      position: "sticky",
      bottom: 0,
      zIndex: 11,
      boxShadow: "0 -6px 12px -8px rgba(0,0,0,.15)",
    },
    button: {
      padding: "12px 20px",
      borderRadius: 10,
      fontSize: 16,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all .2s",
      border: "none",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    primaryButton: {
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      boxShadow: "0 6px 16px rgba(59,130,246,.3)",
    },
    secondaryButton: {
      backgroundColor: "white",
      color: "#374151",
      border: "1px solid #d1d5db",
    },
    disabledButton: {
      backgroundColor: "#e5e7eb",
      color: "#9ca3af",
      cursor: "not-allowed",
    },
    alert: {
      padding: "10px 12px",
      borderRadius: 10,
      fontSize: 14,
      marginBottom: 12,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    errorAlert: {
      backgroundColor: "#fee2e2",
      border: "1px solid #fecaca",
      color: "#dc2626",
    },
    successAlert: {
      backgroundColor: "#dcfce7",
      border: "1px solid #bbf7d0",
      color: "#16a34a",
    },
    summaryCard: {
      backgroundColor: "#f8fafc",
      padding: 16,
      borderRadius: 12,
      border: "1px solid #e2e8f0",
      marginBottom: 16,
    },
    summaryRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: "1px solid #e5e7eb",
    },
    summaryLabel: { fontSize: 14, color: "#6b7280" },
    summaryValue: { fontSize: 14, fontWeight: 500, color: "#111827" },
    spinner: {
      width: 18,
      height: 18,
      border: "2px solid transparent",
      borderTop: "2px solid white",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  const steps = [
    { number: 1, title: "Select Service", icon: Stethoscope },
    { number: 2, title: "Choose Date", icon: Calendar },
    { number: 3, title: "Choose Time", icon: Clock },
    { number: 4, title: "Your Information", icon: User },
    { number: 5, title: "Confirm Booking", icon: CheckCircle },
  ];

  if (success) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={{ padding: 32, textAlign: "center" }}>
            <CheckCircle
              style={{
                width: 64,
                height: 64,
                color: "#10b981",
                marginBottom: 16,
              }}
            />
            <h2 style={{ margin: 0, fontSize: 22 }}>
              Appointment Requested Successfully!
            </h2>
            <p style={{ color: "#6b7280", marginTop: 8 }}>
              Your request has been sent to Dr.{" "}
              {doctor?.name || doctorDoc?.name}. You’ll be notified when it’s
              confirmed.
            </p>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <style>{`
        @keyframes spin { 0% {transform: rotate(0)} 100% {transform: rotate(360deg)} }
        .close-button:hover { background-color: #f3f4f6 !important; }
        .primary-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #4338ca) !important;
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(59,130,246,.4) !important;
        }
        .secondary-button:hover { background-color: #f9fafb !important; }
        .service-card:hover { border-color: #93c5fd !important; background-color: #f0f9ff !important; }
        .date-card:hover { border-color: #93c5fd !important; background-color: #f0f9ff !important; }
        .time-slot:hover:not(.unavailable) { border-color: #93c5fd !important; background-color: #f0f9ff !important; }
        .form-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,.1) !important; }
      `}</style>

      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <h1 style={styles.title}>Book Appointment</h1>
            <button
              style={styles.closeButton}
              className="close-button"
              onClick={onClose}
            >
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>
          <div style={styles.progressBar}>
            {steps.map((s) => (
              <div
                key={s.number}
                style={{
                  ...styles.progressStep,
                  backgroundColor:
                    currentStep >= s.number ? "#3b82f6" : "#e5e7eb",
                }}
              />
            ))}
          </div>
          <div style={styles.stepInfo}>
            Step {currentStep} of {steps.length}:{" "}
            {steps[currentStep - 1]?.title}
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {error && (
            <div style={{ ...styles.alert, ...styles.errorAlert }}>
              <AlertCircle style={{ width: 16, height: 16 }} />
              {error}
            </div>
          )}

          {/* Step 1: Service */}
          {currentStep === 1 && (
            <div>
              <h2 style={styles.stepTitle}>
                <Stethoscope
                  style={{ width: 22, height: 22, color: "#3b82f6" }}
                />
                Select a Service
              </h2>
              <p style={styles.stepDescription}>
                Choose the medical service you’d like to book with Dr.{" "}
                {doctor?.name || doctorDoc?.name}
              </p>

              {(doctorDoc?.services || doctor?.services || []).length > 0 ? (
                (doctorDoc?.services || doctor?.services).map((service, idx) => {
                  const selected = bookingData.serviceName === service.title;
                  return (
                    <div
                      key={service.id || idx}
                      style={{
                        ...styles.serviceCard,
                        ...(selected ? styles.serviceCardSelected : {}),
                      }}
                      className="service-card"
                      onClick={() => {
                        updateBookingData("serviceId", service.id || String(idx));
                        updateBookingData("serviceName", service.title || "");
                        updateBookingData("servicePrice", service.price || "");
                        updateBookingData(
                          "serviceDuration",
                          service.duration || "30 minutes"
                        );
                        if (bookingData.selectedDate) {
                          const dur = parseDurationToMinutes(
                            service.duration || "30 minutes"
                          );
                          buildSlots(bookingData.selectedDate, dur);
                        }
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "start",
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          {service.title}
                        </h3>
                        {service.price && (
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#059669",
                              backgroundColor: "#dcfce7",
                              padding: "4px 8px",
                              borderRadius: 6,
                            }}
                          >
                            {service.price}
                          </span>
                        )}
                      </div>
                      {service.description && (
                        <p
                          style={{
                            fontSize: 14,
                            color: "#6b7280",
                            margin: "8px 0 6px",
                          }}
                        >
                          {service.description}
                        </p>
                      )}
                      {service.duration && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            color: "#9ca3af",
                          }}
                        >
                          <Clock style={{ width: 12, height: 12 }} />
                          Duration: {service.duration}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 20px",
                    backgroundColor: "#f9fafb",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Stethoscope
                    style={{
                      width: 44,
                      height: 44,
                      color: "#9ca3af",
                      marginBottom: 10,
                    }}
                  />
                  <p style={{ color: "#6b7280", margin: 0 }}>
                    No services available.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Date */}
          {currentStep === 2 && (
            <div>
              <h2 style={styles.stepTitle}>
                <Calendar
                  style={{ width: 22, height: 22, color: "#3b82f6" }}
                />
                Choose Date
              </h2>
              <p style={styles.stepDescription}>
                Pick an available date based on the doctor’s schedule.
              </p>

              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                Available Dates
              </h3>
              <div style={styles.dateGrid}>
                {availableDates.length === 0 && (
                  <div
                    style={{
                      gridColumn: "1/-1",
                      textAlign: "center",
                      color: "#6b7280",
                    }}
                  >
                    No upcoming availability.
                  </div>
                )}
                {availableDates.map((d) => (
                  <div
                    key={d.date}
                    style={{
                      ...styles.dateCard,
                      ...(bookingData.selectedDate === d.date
                        ? styles.dateCardSelected
                        : {}),
                    }}
                    className="date-card"
                    onClick={() => {
                      updateBookingData("selectedDate", d.date);
                      updateBookingData("selectedTime", "");
                      const dur = parseDurationToMinutes(
                        bookingData.serviceDuration
                      );
                      buildSlots(d.date, dur);
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                      }}
                    >
                      {d.displayDate}
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      {d.fullDate.getDate()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Time */}
          {currentStep === 3 && (
            <div>
              <h2 style={styles.stepTitle}>
                <Clock
                  style={{ width: 22, height: 22, color: "#3b82f6" }}
                />
                Choose Time
              </h2>
              <p style={styles.stepDescription}>
                Select a free time slot on{" "}
                {bookingData.selectedDate
                  ? new Date(
                      bookingData.selectedDate
                    ).toLocaleDateString(undefined, {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })
                  : "the selected day"}
                .
              </p>

              {!bookingData.selectedDate && (
                <div
                  style={{
                    ...styles.alert,
                    ...styles.errorAlert,
                    marginBottom: 16,
                  }}
                >
                  <AlertCircle style={{ width: 16, height: 16 }} />
                  Please go back and select a date first.
                </div>
              )}

              {bookingData.selectedDate && (
                <>
                  <h3
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      margin: "0 0 10px",
                    }}
                  >
                    Available Times
                  </h3>
                  <div style={styles.timeGrid}>
                    {slots.length === 0 && (
                      <div
                        style={{
                          gridColumn: "1/-1",
                          color: "#6b7280",
                        }}
                      >
                        No free slots on this day.
                      </div>
                    )}
                    {slots.map((slot) => {
                      const selected = bookingData.selectedTime === slot.time;
                      return (
                        <div
                          key={slot.time}
                          style={{
                            ...styles.timeSlot,
                            ...(selected ? styles.timeSlotSelected : {}),
                            ...(!slot.available
                              ? styles.timeSlotUnavailable
                              : {}),
                          }}
                          className={`time-slot ${
                            !slot.available ? "unavailable" : ""
                          }`}
                          onClick={() =>
                            slot.available &&
                            updateBookingData("selectedTime", slot.time)
                          }
                          title={!slot.available ? "Already booked" : ""}
                        >
                          {slot.displayTime}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 4: Patient info */}
          {currentStep === 4 && (
            <div>
              <h2 style={styles.stepTitle}>
                <User
                  style={{ width: 22, height: 22, color: "#3b82f6" }}
                />
                Your Information
              </h2>
              <p style={styles.stepDescription}>
                Provide your contact and visit details.
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>Appointment Type</label>
                <div style={styles.typeSelector}>
                  {[
                    {
                      value: "in-person",
                      label: "In-Person Visit",
                      Icon: Building2,
                      badge: styles.iconInPerson,
                    },
                    {
                      value: "video-call",
                      label: "Video Visit",
                      Icon: Video,
                      badge: styles.iconVideo,
                    },
                    {
                      value: "phone-call",
                      label: "Phone Call",
                      Icon: Phone,
                      badge: styles.iconPhone,
                    },
                  ].map(({ value, label, Icon, badge }) => {
                    const selected = bookingData.appointmentType === value;
                    return (
                      <div
                        key={value}
                        style={{
                          ...styles.typeOption,
                          ...(selected ? styles.typeOptionSelected : {}),
                        }}
                        onClick={() =>
                          updateBookingData("appointmentType", value)
                        }
                      >
                        <span
                          style={{
                            ...styles.iconBadge,
                            ...badge,
                          }}
                        >
                          <Icon size={18} />
                        </span>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        >
                          {label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Contact Phone *</label>
                <input
                  type="tel"
                  className="form-input"
                  style={styles.input}
                  placeholder="+355 69 123 4567"
                  value={bookingData.contactPhone}
                  onChange={(e) =>
                    updateBookingData("contactPhone", e.target.value)
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Reason for Visit *</label>
                <input
                  type="text"
                  className="form-input"
                  style={styles.input}
                  placeholder="e.g., check-up, symptoms, follow-up"
                  value={bookingData.reason}
                  onChange={(e) =>
                    updateBookingData("reason", e.target.value)
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Additional Notes (Optional)</label>
                <textarea
                  className="form-input"
                  style={styles.textarea}
                  placeholder="Anything the doctor should know in advance…"
                  value={bookingData.notes}
                  onChange={(e) =>
                    updateBookingData("notes", e.target.value)
                  }
                />
              </div>
            </div>
          )}

          {/* Step 5: Confirm */}
          {currentStep === 5 && (
            <div>
              <h2 style={styles.stepTitle}>
                <CheckCircle
                  style={{ width: 22, height: 22, color: "#3b82f6" }}
                />
                Confirm Your Appointment
              </h2>
              <p style={styles.stepDescription}>
                Review the details before submitting.
              </p>

              <div style={styles.summaryCard}>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Doctor</span>
                  <span style={styles.summaryValue}>
                    Dr. {doctor?.name || doctorDoc?.name}
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Specialty</span>
                  <span style={styles.summaryValue}>
                    {doctor?.specialty || doctorDoc?.specialty}
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Service</span>
                  <span style={styles.summaryValue}>
                    {bookingData.serviceName}
                  </span>
                </div>
                {bookingData.servicePrice && (
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>Price</span>
                    <span style={styles.summaryValue}>
                      {bookingData.servicePrice}
                    </span>
                  </div>
                )}
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Date</span>
                  <span style={styles.summaryValue}>
                    {new Date(
                      bookingData.selectedDate
                    ).toLocaleDateString(undefined, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Time</span>
                  <span style={styles.summaryValue}>
                    {new Date(
                      `2000-01-01T${bookingData.selectedTime}:00`
                    ).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Type</span>
                  <span style={styles.summaryValue}>
                    {bookingData.appointmentType === "in-person"
                      ? "In-Person Visit"
                      : bookingData.appointmentType === "video-call"
                      ? "Video Call"
                      : "Phone Call"}
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Contact</span>
                  <span style={styles.summaryValue}>
                    {bookingData.contactPhone}
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Reason</span>
                  <span style={styles.summaryValue}>
                    {bookingData.reason}
                  </span>
                </div>
                {bookingData.notes && (
                  <div
                    style={{
                      ...styles.summaryRow,
                      borderBottom: "none",
                    }}
                  >
                    <span style={styles.summaryLabel}>Notes</span>
                    <span style={styles.summaryValue}>
                      {bookingData.notes}
                    </span>
                  </div>
                )}
              </div>

              <div
                style={{
                  backgroundColor: "#fef3c7",
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid #fde68a",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    color: "#92400e",
                    fontSize: 13,
                  }}
                >
                  <AlertCircle style={{ width: 16, height: 16 }} />
                  You’ll receive confirmation once the doctor reviews your
                  request.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div>
            {currentStep > 1 && (
              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                }}
                className="secondary-button"
                onClick={() => setCurrentStep((s) => s - 1)}
                disabled={loading}
              >
                <ArrowLeft style={{ width: 16, height: 16 }} />
                Previous
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            {currentStep < steps.length ? (
              <button
                style={{
                  ...styles.button,
                  ...(validateStep(currentStep)
                    ? styles.primaryButton
                    : styles.disabledButton),
                }}
                className="primary-button"
                onClick={() => setCurrentStep((s) => s + 1)}
                disabled={!validateStep(currentStep) || loading}
              >
                Next
                <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            ) : (
              <button
                style={{
                  ...styles.button,
                  ...(loading
                    ? styles.disabledButton
                    : styles.primaryButton),
                }}
                className="primary-button"
                onClick={submitBooking}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    Booking…
                  </>
                ) : (
                  <>
                    <CheckCircle style={{ width: 16, height: 16 }} />
                    Confirm Appointment
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentBooking;
